import { Spin } from "antd";
import * as React from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";

import { CancellationToken } from "lib/api/base";
import { IBaseProps } from "lib/data/interfaces";

import { Permission } from "lib/data/model";
import { PermissionService, StateService } from "lib/services";
import { LoginService } from "lib/services/LoginService";
import { Event, EventDispatcher } from "lib/EventDispatcher";


interface ISecureProps extends IBaseProps, RouteComponentProps<any> {
	permission?: Permission;
	onRedirect?(): void;
	onDenial?(): void;
}

interface ISecureState extends IBaseProps {
	isLoading: boolean;
	isDisplaying: boolean;
	isAuthenticated: boolean;
	isAuthorized: boolean;
}


export class SecurePage extends React.Component<ISecureProps, ISecureState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	constructor(props: ISecureProps) {

		super(props);

		this.state = {
			isLoading: true,
			isDisplaying: false,
			isAuthenticated: true,
			isAuthorized: true
		};
	}

	public async componentWillMount() {

		// Process Login
		await this.processLogin(this.props.permission);

		// Register For Logged In Event
		EventDispatcher.register(Event.LoggedIn, this.onLoginChanged);

		// Register For Logged Out Event
		EventDispatcher.register(Event.LoggedOut, this.onLoginChanged);

		// Register For Unauthorized Event
		EventDispatcher.register(Event.Unauthorized, this.onUnauthorized);
	}

	public async componentWillReceiveProps(newProps: ISecureProps) {

		// Process Login
		await this.processLogin(newProps.permission);
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();

		// Unregister For Logged In Event
		EventDispatcher.unregister(Event.LoggedIn, this.onLoginChanged);

		// Unregister For Logged Out Event
		EventDispatcher.unregister(Event.LoggedOut, this.onLoginChanged);

		// Unregister For Unauthorized Event
		EventDispatcher.unregister(Event.Unauthorized, this.onUnauthorized);
	}

	public render() {

		// Check If Component Is Loading
		if (this.state.isLoading) {

			return (
				<Spin />
			);
		}
		else if (!this.state.isAuthenticated) {

			return (
				<Redirect push={true} to="/login" />
			);
		}
		else if (!this.state.isAuthorized) {

			return (
				"Unauthorized"
			);
		}
		else if (!this.state.isDisplaying) {

			return (
				"Error"
			);
		}
		else {

			return this.props.children;
		}
	}

	private processLogin = async (permission?: Permission) => {

		// Create Permitted Flag
		let isPermitted: boolean = true;

		// Get Logged In Status
		const isLoggedIn = await LoginService.isLoggedIn();

		// Check Logged In Status
		if (isLoggedIn) {

			// Get Currently Logged In User
			const userLoggedIn = await LoginService.getUserIfExists();

			// Check User
			if (userLoggedIn !== undefined) {

				// Check For Permission
				if (permission !== undefined) {

					// Check Permissions
					isPermitted = await PermissionService.userHasPermission(userLoggedIn, permission);
				}

				// Check Whether Permitted
				if (isPermitted) {

					// Indicate Display Can Occur
					StateService.setCancelableState(() => this.setState({
						isLoading: false,
						isDisplaying: true
					}), this.apiCancellationToken);

					return;
				}
				else {

					// Indicate Component Is NOT Authorized
					StateService.setCancelableState(() => this.setState({
						isLoading: false,
						isAuthorized: false
					}), this.apiCancellationToken);

					return;
				}
			}
		}
		else {

			// Set Current Location
			LoginService.setPreviousLocation(this.props.location);

			// Indicate Component Is NOT Authenticated
			StateService.setCancelableState(() => this.setState({
				isLoading: false,
				isAuthenticated: false
			}), this.apiCancellationToken);

			return;
		}

		// Indicate Component Is NOT Loading
		StateService.setCancelableState(() => this.setState({
			isLoading: false
		}), this.apiCancellationToken);
	}

	private onLoginChanged = async () => {

		// Re-Process Permission
		await this.processLogin();
	}

	private onUnauthorized = async () => {

		// Indicate Component Is NOT Authenticated
		StateService.setCancelableState(() => this.setState({
			isLoading: false,
			isAuthenticated: false
		}), this.apiCancellationToken);
	}
}
